import { useId } from "react";

/**
 * A stacked bar chart in the portal's chart palette (requirements 8.9,
 * mock-ups of 2026-09-18): pastel fills at about L 0.90 read as area
 * color, and a saturated 1.5px outline of the same hue carries the 3:1
 * WCAG 1.4.11 asks of a graphical object - so the series stay distinct in
 * grayscale and for color-blind readers, and text never sits on a
 * pastel. Drawn as SVG with a visually hidden table beneath it.
 */
export interface Series {
  key: string;
  label: string;
  /** Fill and outline: one hue, two lightnesses. */
  fill: string;
  stroke: string;
}

export const OUTCOME_SERIES: Series[] = [
  { key: "contained", label: "Contained", fill: "#cfe3f7", stroke: "#1f56b8" },
  { key: "answered", label: "Answered", fill: "#d6efe1", stroke: "#0f6e4a" },
  { key: "abandoned", label: "Abandoned", fill: "#fbe3c8", stroke: "#9a5a12" },
  { key: "transferredOut", label: "Transferred out", fill: "#e8e1f5", stroke: "#5b3fa0" },
];

export function StackedBars({
  rows,
  series,
  label,
  formatX,
  height = 220,
}: {
  rows: Array<Record<string, unknown> & { date: string }>;
  series: Series[];
  label: string;
  formatX: (date: string) => string;
  height?: number;
}) {
  const id = useId();
  const width = 760;
  const left = 44;
  const bottom = 26;
  const top = 12;
  const plotH = height - top - bottom;
  const max = Math.max(1, ...rows.map((r) => series.reduce((a, s) => a + Number(r[s.key] ?? 0), 0)));
  const nice = niceMax(max);
  const gap = 6;
  const barW = rows.length ? Math.max(4, (width - left - 8) / rows.length - gap) : 0;
  const y = (v: number) => top + plotH - (v / nice) * plotH;
  const ticks = [0, nice / 3, (2 * nice) / 3, nice];
  const labelEvery = Math.max(1, Math.ceil(rows.length / 6));

  return (
    <figure>
      <div className="mb-2 flex flex-wrap items-center justify-end gap-3 text-xs text-neutral-600 dark:text-neutral-400">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="inline-block h-3 w-3 rounded-sm" style={{ background: s.fill, border: `1.5px solid ${s.stroke}` }} />
            {s.label}
          </span>
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full" role="img" aria-labelledby={`${id}-title`}>
        <title id={`${id}-title`}>{label}</title>
        <g stroke="currentColor" strokeOpacity="0.15" strokeWidth="1">
          {ticks.map((t) => (
            <line key={t} x1={left} x2={width - 4} y1={y(t)} y2={y(t)} />
          ))}
        </g>
        <g fontSize="11" fill="currentColor" fillOpacity="0.7">
          {ticks.map((t) => (
            <text key={t} x={left - 6} y={y(t) + 4} textAnchor="end">
              {compact(t)}
            </text>
          ))}
        </g>
        <g strokeWidth="1.5">
          {rows.map((r, i) => {
            let acc = 0;
            const x = left + 4 + i * (barW + gap);
            return (
              <g key={r.date}>
                {series.map((s) => {
                  const v = Number(r[s.key] ?? 0);
                  const y0 = y(acc + v);
                  const h = y(acc) - y0;
                  acc += v;
                  return v > 0 ? <rect key={s.key} x={x} y={y0} width={barW} height={h} fill={s.fill} stroke={s.stroke} /> : null;
                })}
              </g>
            );
          })}
        </g>
        <g fontSize="11" fill="currentColor" fillOpacity="0.7">
          {rows.map((r, i) =>
            i % labelEvery === 0 ? (
              <text key={r.date} x={left + 4 + i * (barW + gap)} y={height - 8}>
                {formatX(r.date)}
              </text>
            ) : null,
          )}
        </g>
      </svg>
      {/* The wrapper is sr-only, not the table: a table ignores the 1px width and overflow, and pushed the page sideways at phone width (#416). */}
      <div className="sr-only">
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">Day</th>
              {series.map((s) => (
                <th key={s.key} scope="col">{s.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date}>
                <td>{r.date}</td>
                {series.map((s) => (
                  <td key={s.key}>{Number(r[s.key] ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

function niceMax(v: number): number {
  const p = 10 ** Math.floor(Math.log10(v));
  const m = v / p;
  const step = m <= 1 ? 1 : m <= 2 ? 2 : m <= 3 ? 3 : m <= 4 ? 4 : m <= 5 ? 5 : m <= 6 ? 6 : m <= 8 ? 8 : 10;
  return step * p;
}

function compact(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k`;
  return String(Math.round(v));
}
